import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, ReactNode } from "react";

// Reusable on-brand confirm dialog — replaces the remaining window.confirm calls
// (Checklist reset/delete, TeamSettings remove driver). Same idiom as
// SeriesAdminModals: portal, overlay click-to-cancel, focus trap, ESC cancels,
// scroll lock, focus restore. The confirm button gets initial focus so Enter
// confirms; `tone="danger"` puts the red flag on the confirm button only.

export default function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  tone = "default",
  onConfirm,
  onCancel,
  loading = false,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  tone?: "default" | "danger";
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  // Refs so inline handlers at call sites don't re-run the mount effect every render.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    if (!open) return;
    const prevActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the dialog, NOT the destructive confirm button — Enter must not
    // confirm a delete before the user reads it (APG guidance for destructive
    // dialogs). Tab moves to Cancel (first control) then Confirm.
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loadingRef.current) { e.preventDefault(); onCancelRef.current(); return; }
      if (e.key !== "Tab") return;
      const list = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])') || []);
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div style={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !loading && onCancel()}>
      <div ref={dialogRef} className="card" style={styles.modal} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <div style={styles.head}>
          <h2 style={styles.title}>{title}</h2>
          <p style={styles.dek}>{body}</p>
        </div>
        <div style={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            ref={confirmRef}
            type="button"
            className={tone === "danger" ? "btn btn-danger" : "btn btn-primary"}
            onClick={onConfirm}
            disabled={loading}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6,8,12,0.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4.5rem 1rem", overflowY: "auto" },
  modal: { width: "100%", maxWidth: "460px", padding: "1.6rem 1.6rem 1.3rem", display: "flex", flexDirection: "column", gap: "1rem" },
  head: { display: "flex", flexDirection: "column", gap: "0.3rem" },
  title: { fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, margin: 0, color: "var(--text-strong)" },
  dek: { margin: 0, fontSize: "0.9rem", color: "var(--text-muted)", lineHeight: 1.45 },
  actions: { display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.4rem" },
};
